import type { MouseEvent } from 'react';
import React from 'react';
import { Breakpoint } from '@noths/polaris-client-ribbons-base';
import { Picture, PictureSource } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';

import * as styles from './HeroCarouselItem.styles';

export interface HeroCarouselItemProps {
  alt: string;
  /** Heading element to render. The active slide is an `h1`; the rest are `h2`. Defaults to `h2`. */
  headingLevel?: 'h1' | 'h2';
  headingLine1: string;
  headingLine2?: string;
  headingPosition: 'top' | 'bottom';
  /** When set, the whole image links here — used for the active slide's CTA destination. */
  href?: string;
  // Desktop image, cropped wide (3:1), used at/above the L breakpoint.
  image: string;
  // Mobile image, cropped tall (3:4), used below the L breakpoint. Falls back to `image` if omitted.
  imageMobile?: string;
  onImageClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  priority?: boolean;
}

/**
 * The sliding part of a hero slide: the image with its heading overlay. The paragraph, T&Cs and
 * CTA are rendered separately by `HeroCarouselCaption` so they can swap in place rather than slide.
 *
 * Uses the design-system `Picture`: `PictureSource` applies a `min-width` query, so the wide (3:1)
 * desktop crop sits in a `min-width: L` source and the tall (3:4) mobile crop is the default `src`.
 * The first slide loads eagerly via `lazyLoading={false}`.
 */
export const HeroCarouselItem = ({
  alt,
  headingLevel = 'h2',
  headingLine1,
  headingLine2,
  headingPosition,
  href,
  image,
  imageMobile,
  onImageClick,
  priority = false,
}: HeroCarouselItemProps) => {
  // Keep h1/h2 as literal tags rather than a dynamic `<Heading>`: babel-plugin-styled-components
  // hoists a `styled(...)` wrapper for the `css` prop to module scope, where a tag held in a local
  // variable isn't in scope (ReferenceError at runtime).
  const isHeadingTop = headingPosition === 'top';
  const headingCss = [styles.heading, isHeadingTop ? styles.headingTop : styles.headingBottom];
  const headingContent = (
    <>
      {/* Decorative blur layer behind the gradient; masked so it fades into the image (see styles). */}
      <div
        aria-hidden="true"
        css={[styles.blurLayer, isHeadingTop ? styles.blurLayerTop : styles.blurLayerBottom]}
      >
        <div css={styles.blurInner} />
      </div>
      <span>{headingLine1}</span>
      {headingLine2 && <span>{headingLine2}</span>}
    </>
  );

  return (
    <div css={styles.imageWrapper}>
      {/*
       * The anchor is always rendered but only links when `href` is set (the active slide), so the
       * image is never remounted as slides change — wrapping/unwrapping `<Picture>` would reset its
       * load state and flash the placeholder. The heading sibling is `pointer-events: none` so the
       * whole image, including the area behind the heading, is clickable.
       */}
      <Picture
        alt={alt}
        fetchPriority={priority ? 'high' : undefined}
        imgCss={styles.image}
        lazyLoading={!priority}
        src={imageMobile ?? image}
      >
        <PictureSource minBreakpoint={Breakpoint.L} srcSet={image} />
      </Picture>
      {/*
       * Active slide only: a stretched link over the image (kept separate from <Picture> so the
       * image never remounts, and so inactive slides render no anchor at all). Labelled by the
       * image alt via the visually-hidden span.
       */}
      {href && (
        <a css={styles.imageLink} href={href} onClick={onImageClick}>
          <span css={visuallyHidden}>{alt}</span>
        </a>
      )}
      {headingLevel === 'h1' ? (
        <h1 css={headingCss}>{headingContent}</h1>
      ) : (
        <h2 css={headingCss}>{headingContent}</h2>
      )}
    </div>
  );
};
